import type { de } from "./de";

export const en: typeof de = {
  seo: {
    ogLocale: "en_US",
    home: {
      title: "The 4-Day Week in Banking | Whitepaper 2026",
      description: "How banks solve the skills shortage with the 4-day week: case studies, the AI dividend and employer attractiveness. Free whitepaper.",
    },
    businessCase: {
      title: "Request a Business Case | The 4-Day Week in Banking",
      description: "Individual business case on the 4-day week for your bank: submit a request and receive a tailored analysis from Intraprenör.",
    },
  },
  hero: {
    badge: "Whitepaper 2026",
    title1: "The 4-Day Week",
    title2: "in Banking",
    subtitle: "Between employer attractiveness and the AI dividend: how banks solve the skills shortage with the 4-day week",
    downloadButton: "Download whitepaper",
    learnMore: "Learn more",
  },
  stats: {
    title: "The numbers",
    titleItalic: "speak for themselves",
    subtitle: "International studies and real-world examples show: the 4-day week is no longer an experiment.",
    items: [
      { suffix: "%", label: "Continuation", description: "of companies in the German pilot project continue the 4-day week" },
      { suffix: "x", label: "More applications", description: "Fivefold increase in applications at PSD Bank Braunschweig" },
      { suffix: "%", label: "Fewer sick days", description: "Reduction in sickness-related absence at Atom Bank (UK)" },
    ],
  },
  cases: {
    title1: "The 4-day week pioneers:",
    title2: "4 banking cases that show",
    titleItalic: "how it can work",
    subtitle: "These banks prove it: the 4-day week works – from the digital neobank to the traditional savings bank.",
    resultsLabel: "Results",
    items: [
      {
        type: "The Digital Pioneer",
        highlight: "68% fewer sick days",
        model: "4-day week at full pay since 2021",
        results: [

          "49% more applications",
          "68% fewer sick days",
          "Staff turnover down 57%",
        ],
      },
      {
        type: "The Regional Cooperative Bank",
        highlight: "All positions filled",
        model: "4-day week as a pilot project",
        results: [
          "Fivefold increase in applications",
          "All vacant positions successfully filled",
          "Significant reduction in sick days",
          "High employee satisfaction",
        ],
        quote: {
          text: "In the past we barely got any responses to a job posting. Today we receive 50 to 60 applications for every advertisement.",
          author: "Carsten Graf",
          role: "Chairman of the Board, PSD Bank Braunschweig",
        },
      },
      {
        type: "The Solid Innovator",
        highlight: "Most attractive employer in the region",
        model: "4-day week since 2024",
        results: [
          "Employees switching from major banks (Credit Suisse, UBS)",
          "Most attractive employer in the rural region",
          "Productivity maintained at the same level",
          "Strong positioning in the competition for talent",
        ],
      },
      {
        type: "The Flexible Large Savings Bank",
        highlight: "Freedom of choice for employees",
        model: "4-day week as part of the 'WertSchätze' concept",
        results: [
          "4-day week offered as an option",
          "Part of a comprehensive appreciation concept",
          "Flexible working-time models for different life stages",
          "Increased employee retention",
        ],
      },
    ],
  },
  trends: {
    badge: "4 drivers",
    title: "Why the 4-day week",
    titleItalic: "works",
    subtitle: "Four key factors make the 4-day week a success model for banks.",
    items: [
      {
        title: "Attractiveness & employee retention",
        subtitle: "The recruiting revolution",
        stats: [
          { value: "5x", label: "More applications" },
          { value: "57%", label: "Less turnover" },
        ],
        description: "The 4-day week is the most effective remedy against the skills shortage in the banking sector. Banks report a fivefold increase in applications and a dramatic reduction in turnover.",
        insight: "Not a single employee has left us – Hans-Jürgen Lembicz, Volksbank Euskirchen",
      },
      {
        title: "Employee health",
        subtitle: "Less stress, more well-being",
        stats: [
          { value: "68%", label: "Fewer sick days" },
          { value: "89 min", label: "Less stress/week" },
        ],
        description: "The additional recovery time leads to measurable improvements in physical and mental health. Sick days drop drastically, burnout rates decline.",
        insight: "Employees gain back an average of 89 minutes of less stressful working time per week.",
      },
      {
        title: "Costs & efficiency",
        subtitle: "The business case",
        stats: [
          { value: "65%", label: "Optimized processes" },
          { value: "100%", label: "Productivity maintained" },
        ],
        description: "Less working time does not mean less output. Through process optimization and increased focus, productivity stays at the same level.",
        insight: "65% of German pilot companies have eliminated unnecessary meetings, tasks and processes through the 4-day week.",
      },
      {
        title: "Artificial intelligence",
        subtitle: "The AI dividend",
        stats: [
          { value: "3.5", label: "Days according to JPMorgan" },
          { value: "30%", label: "Potential time savings" },
        ],
        description: "AI-driven automation in the banking sector creates room for shorter working hours. JPMorgan CEO Jamie Dimon predicts: the 3.5-day week will arrive within a generation.",
        insight: "AI and automation make it possible to give productivity gains back to employees as free time.",
      },
    ],
  },
  quotes: {
    title: "Voices",
    titleItalic: "from practice",
    subtitle: "What executives report about their experiences with the 4-day week.",
    items: [
      {
        text: "Not a single employee has left us. Normally we always have a turnover of about five percent, but right now it is at zero.",
        author: "Hans-Jürgen Lembicz",
        role: "Board member",
        company: "Volksbank Euskirchen",
      },
      {
        text: "Within the first six months our productivity rose. No losses, on the contrary: greater focus, more employer attractiveness.",
        author: "Hans Arnet",
        role: "CEO",
        company: "Raiffeisenbank Luzerner Landschaft Nordwest",
      },
      {
        text: "In the past we barely got any responses to a job posting. Today we receive 50 to 60 applications for every advertisement – and that applies to all areas.",
        author: "Carsten Graf",
        role: "Chairman of the Board",
        company: "PSD Bank Braunschweig",
      },
    ],
  },
  dataTable: {
    title: "Banks in the DACH region",
    subtitle: "Overview of all banks with 4-day week initiatives in Germany, Austria and Switzerland.",
    headers: { bank: "Bank", country: "Country", model: "Model", hours: "Hours", status: "Status", result: "Result" },
    statusLabels: { aktiv: "Active", pilot: "Pilot", geplant: "Planned" },
    items: [
      { name: "Atom Bank", country: "UK", model: "4 days at full pay", hours: "34h", status: "aktiv" as const, highlight: "68% fewer sick days" },
      { name: "PSD Bank Braunschweig", country: "DE", model: "4-day week or\n4.5-day week", hours: "32-36h", status: "aktiv" as const, highlight: "All positions filled" },
      { name: "Raiffeisenbank Luzerner Landschaft Nordwest", country: "CH", model: "4-day week", hours: "38h", status: "aktiv" as const, highlight: "Lower turnover" },
      { name: "Raiffeisenbank Surselva", country: "CH", model: "4-day week", hours: "38h", status: "aktiv" as const, highlight: "Higher attractiveness" },
      { name: "Raiffeisenbank Appenzeller Hinterland", country: "CH", model: "4-day week or\n4.5-day week", hours: "38h", status: "aktiv" as const, highlight: "Received applications from major-bank employees" },
      { name: "Sparkasse zu Lübeck", country: "DE", model: "WertSchätze model", hours: "Flexible", status: "aktiv" as const, highlight: "Freedom of choice for all" },
      { name: "Volksbank Euskirchen", country: "DE", model: "4-day week", hours: "35h", status: "aktiv" as const, highlight: "0% turnover" },
      { name: "Volksbank Kaiserslautern", country: "DE", model: "4-day week", hours: "36h", status: "pilot" as const, highlight: "Pilot project running" },
      { name: "Eckernförder Bank", country: "DE", model: "4 days optional", hours: "32-40h", status: "aktiv" as const, highlight: "Flexible model" },
      { name: "VR Bank Niebüll", country: "DE", model: "4-day week", hours: "34h", status: "pilot" as const, highlight: "High employee satisfaction" },
    ],
  },
  download: {
    title: "Whitepaper",
    titleSuffix: " download",
    subtitle: "Receive the complete whitepaper with all case studies, statistics and the implementation guide for the 4-day week in banking.",
    bulletPoints: [
      "Detailed case studies of all pioneer banks",
      "Statistics and research findings",
      "3-phase implementation guide",
      "ROI calculations and business case",
    ],
    formLabels: {
      name: "Name *",
      namePlaceholder: "John Doe",
      email: "Email address *",
      emailPlaceholder: "john@example.com",
      company: "Company (optional)",
      companyPlaceholder: "Your bank / company",
      position: "Position (optional)",
      positionPlaceholder: "e.g. Board member, Head of HR",
      submit: "Download for free",
      submitting: "Sending...",
      disclaimer: "By downloading, you accept our privacy policy.",
    },
    success: {
      title: "Thank you!",
      description: "Click the button to download the whitepaper as a PDF:",
      downloadButton: "Download PDF",
    },
    whitepaperLanguageNote: "🌐 The whitepaper is only available in German",
    toast: {
      successTitle: "Thank you!",
      successDescription: "Your download will start shortly.",
      errorTitle: "Error",
      errorDescription: "There was a problem. Please try again.",
    },
    validation: {
      nameMin: "Name must be at least 2 characters",
      emailInvalid: "Please enter a valid email address",
    },
  },
  event: {
    badge: "Live webinar",
    languageNote: "In German",
    title: "More productivity in banking through shorter working hours",
    lead: "While Helaba boss Thomas Groß demands more work and Deutsche Bank CEO Christian Sewing calls for more drive, other banks are already delivering – with fewer working days.",
    date: "Tue, 5 May 2026, 12:00 PM",
    context: "Skills shortages and demographic change hit banks hard, especially in rural areas. Flexible working models are no longer a nice-to-have, but a strategic instrument to stay relevant as an employer – while delivering results at the same time.",
    contextProof: "PSD Bank Braunschweig has raised its lending volume target to 130 million euros – with a 4-day week. Raiffeisenbank Appenzeller Hinterland was one of the first banks in Switzerland to pilot the model – with measurable effects on employee satisfaction and employer attractiveness.",
    speakersTitle: "Our guests",
    speakers: [
      { name: "Simon Frehner", role: "Head of Bank Management, Raiffeisenbank Appenzeller Hinterland" },
      { name: "Carsten Graf", role: "Spokesperson of the Board, PSD Bank Braunschweig" },
    ],
    hostsLabel: "Moderation",
    hosts: [
      { name: "Carsten Meier", role: "4-Day Week Germany Initiative", url: "https://www.4tagewoche.org" },
      { name: "Veit Hailperin", role: "4-Day Week Switzerland & Austria", url: "https://www.hailperin.ch" },
    ],
    agendaTitle: "What to expect",
    agenda: [
      "How does implementation in banking really work?",
      "What does it bring for recruiting and employee retention – and what doesn't?",
      "Which flexible models work, which fail?",
      "Tough questions on productivity, sick leave and customer experience",
    ],
    audience: "For boards, management and HR leaders in banks and financial institutions.",
    duration: "60 min.",
    format: "Online",
    price: "Free participation",
    signupButton: "Register for free now",
    linkedinButton: "View on LinkedIn",
  },
  contact: {
    fazitTitle: "The right time for bold steps",
    fazitText: "The 4-day week is no longer a vision of the future – it is a lived reality in forward-looking banks. Whether as a strategic answer to the skills shortage, an investment in employee health or preparation for the AI transformation: now is the right time.",
    phasesTitle: "Your path to the 4-day week",
    phases: [
      { number: "01", title: "Management workshop", description: "Analysis of the starting situation and development of a tailored concept" },
      { number: "02", title: "Piloting", description: "Guided introduction in selected areas with continuous evaluation" },
      { number: "03", title: "Rollout", description: "Gradual expansion to the entire company with success measurement" },
    ],
    ctaTitle: "Ready for the next step?",
    ctaText: "Arrange a non-binding introductory meeting and find out how the 4-day week can also work in your bank.",
    downloadButton: "Download whitepaper",
    contacts: [
      { name: "Carsten Meier", role: "4-Day Week Germany" },
      { name: "Veit Hailperin", role: "4-Day Week Switzerland & 4-Day Week Austria" },
    ],
    impressum: "Imprint",
    datenschutz: "Privacy",
    copyright: "© 2026 4tagewoche.org – Consulting for working-time innovation",
  },
  businessCase: {
    backToReport: "Back to report",
    badge: "Free offer",
    heroTitle: "Is the 4-day week worth it for my company?",
    heroSubtitle: "We calculate your individual banking case – data-based.",
    intro1: "Based on our experience from numerous consulting projects and our data on banks in the DACH region, we create an individual business case for your institution.",
    intro2: "In a 30-minute call, we want to better understand your case. Afterwards, we provide you with a tailored business case to discuss internally on a sound basis whether a 4-day week makes sense for you.",
    benefits: [
      { title: "30-minute call", desc: "We understand your starting position, your goals and your concerns." },
      { title: "Individual business case", desc: "Concrete figures for your institution – productivity, recruiting, sick leave." },
      { title: "Basis for discussion", desc: "A document you can work with in the board and with the supervisory board." },
    ],
    formTitle: "Request a business case",
    formSubtitle: "Tell us briefly about your situation – we will get back to you within two business days.",
    fields: {
      name: "Name",
      namePlaceholder: "First and last name",
      position: "Position",
      positionPlaceholder: "e.g. Board member, Head of HR",
      company: "Company",
      companyPlaceholder: "Name of your institution",
      situation: "Starting situation",
      situationPlaceholder: "What does your current staffing and working-time situation look like? What challenges are driving you?",
      perspective: "Your current perspective on the 4-day week",
      perspectivePlaceholder: "Where do you stand on the topic today? What are open questions, what are concerns?",
      privacy: "I accept the privacy policy and agree that my information will be stored for processing the request.",
      submit: "Request a business case",
      submitting: "Sending …",
    },
    validation: {
      nameMin: "Please enter your name.",
      positionMin: "Please enter your position.",
      companyMin: "Please enter your company.",
      situationMin: "Please describe your starting situation in at least one sentence.",
      perspectiveMin: "Please describe your perspective in at least one sentence.",
      privacyRequired: "Please accept the privacy policy.",
    },
    success: {
      title: "Thank you for your request!",
      description: "We will get back to you within two business days to arrange a 30-minute call.",
      backButton: "Back to report",
    },
    toast: {
      successTitle: "Request sent",
      successDescription: "We will get back to you shortly.",
      errorTitle: "Something went wrong",
      errorDescription: "Please try again or write to us directly by email.",
    },
  },
};
